import {
  AuthenticationDetails,
  CognitoUser, CognitoUserAttribute,
  CognitoUserPool,
} from 'amazon-cognito-identity-js';
import config from '../config';
import AWS from 'aws-sdk';
import store, {actions} from '../store';
import _ from 'lodash';

const validUserAttributes = [
  'picture',
  'nickname',
  'family_name',
  'email',
  'phone_number',
  'profile',
];

class Auth {

  getCognitoUser() {
    const userPool = new CognitoUserPool({
      UserPoolId: config.cognito.USER_POOL_ID,
      ClientId: config.cognito.APP_CLIENT_ID,
    });
    return userPool.getCurrentUser();
  }

  saveUser(profile, token) {
    this.userProfile = _.pick(profile, validUserAttributes);
    this.userProfile.id = profile.sub;
    this.userProfile.isAdmin = _.isArray(profile['cognito:groups']) &&
        profile['cognito:groups'].includes('admin');
    this.userProfile.picture = profile.picture || '/img/profile.svg';
    this.userToken = token;
  }

  login({email, password, remember = true, newPassword = null, mfaCode = null}) {
    const userPool = new CognitoUserPool({
      UserPoolId: config.cognito.USER_POOL_ID,
      ClientId: config.cognito.APP_CLIENT_ID,
    });
    const cognitoUser = new CognitoUser({Username: email, Pool: userPool});

    return new Promise((resolve, reject) => {

      const transformToUser = (session) => {
        this.saveUser(
            session.getIdToken().decodePayload(),
            session.getIdToken().getJwtToken(),
        );
        resolve({});
      };

      let self = this;
      let params = {
        onSuccess(result) {
          if (!remember) {
            cognitoUser.clearCachedTokens();
          }
          transformToUser(result);
        },
        onFailure(err) {
          reject(err);
        },
        newPasswordRequired(userAttributes, requiredAttributes) {
          self.cognitoUser = self.cognitoUser || cognitoUser;
          resolve({newPasswordRequired: true});
        },
        mfaRequired(codeDeliveryDetails) {
          self.cognitoUser = self.cognitoUser || cognitoUser;
          resolve({mfaRequired: true});
        },
      };

      if (mfaCode && this.cognitoUser) {
        this.cognitoUser.sendMFACode(mfaCode, {
          onSuccess(result) {
            transformToUser(result);
          },
          onFailure(err) {
            reject(err);
          },
        });
      } else if (newPassword && this.cognitoUser) {
        this.cognitoUser.completeNewPasswordChallenge(newPassword,
            {
              preferred_username: email,
            }, params);
      } else {
        const authDetails = new AuthenticationDetails({
          Username: email,
          Password: password,
        });
        cognitoUser.authenticateUser(authDetails, params);
      }

    });

  }

  async user(force = false) {
    if (!this.userProfile || force) {
      const cognitoUser = this.getCognitoUser();
      if (!cognitoUser) return null;
      const user = await this.getUserToken(cognitoUser);
      await this.fetchAwsCredentials(user.token);

      this.saveUser(user.profile, user.token);
    }

    return this.userProfile;

  }

  check() {
    return store.getState().user !== null;
  }

  getUserToken(cognitoUser) {
    return new Promise((resolve, reject) => {
      if (!cognitoUser) {
        resolve(null);
      }
      cognitoUser.getSession(function(err, session) {
        if (err) {
          reject(err);
          return;
        }
        let user = {
          profile: session.getIdToken().decodePayload(),
          token: session.getIdToken().getJwtToken(),
        };

        cognitoUser.getUserAttributes((err, attributes) => {
          user.profile = Object.assign({}, user.profile,
              _.reduce(attributes, (container, item) => {
                container[item.Name] = item.Value;
                return container;
              }, {}));
          resolve(user);
        });

      });
    });
  }

  fetchAwsCredentials(token) {
    const authenticator = `cognito-idp.${config.cognito.REGION}.amazonaws.com/${config.cognito.USER_POOL_ID}`;

    AWS.config.update({region: config.cognito.REGION});

    AWS.config.credentials = new AWS.CognitoIdentityCredentials({
      IdentityPoolId: config.cognito.IDENTITY_POOL_ID,
      Logins: {
        [authenticator]: token,
      },
    });

    return AWS.config.credentials.getPromise();
  }

  logout() {
    const cognitoUser = this.getCognitoUser();

    if (cognitoUser !== null) {
      cognitoUser.signOut();
    }

    if (AWS.config.credentials) {
      AWS.config.credentials.clearCachedId();
      AWS.config.credentials = new AWS.CognitoIdentityCredentials({});
    }
  }

  update(attributes,passwordSet) {
    const cognitoUser = this.getCognitoUser();

    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err, session) => {
        cognitoUser.updateAttributes(
            _.reduce(attributes, (container, value, key) => {
              if (validUserAttributes.includes(key)) {
                container.push(new CognitoUserAttribute({
                  Name: key,
                  Value: value,
                }));
              }
              return container;
            }, []), (err, result) => {
              if (err) {
                reject(err);
                return;
              }
              if(passwordSet){
                cognitoUser.changePassword(passwordSet.oldPassword,passwordSet.newPassword,(err, result)=>{
                  if (err) {
                    reject(err);
                    return;
                  }
                  resolve();
                })
              } else {
                resolve();
              }
            });
      });
    });
  }

  changePassword(oldPassword, newPassword) {
    const cognitoUser = this.getCognitoUser();

    return new Promise((resolve, reject) => {
      cognitoUser.changePassword(oldPassword, newPassword, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve();
      });
    });

  }

  forgotPassword(email) {
    const userPool = new CognitoUserPool({
      UserPoolId: config.cognito.USER_POOL_ID,
      ClientId: config.cognito.APP_CLIENT_ID,
    });
    const cognitoUser = new CognitoUser({Username: email, Pool: userPool});

    const self = this;
    return new Promise((resolve, reject) => {
      cognitoUser.forgotPassword({
        onSuccess(result) {
          self.cognitoUser = self.cognitoUser || cognitoUser;
          resolve(result);
        },
        onFailure: reject,
      });
    });
  }

  setPasswordFromForgotPassword(mfaCode, newPassword) {
    const cognitoUser = this.cognitoUser;

    return new Promise((resolve, reject) => {
      cognitoUser.confirmPassword(mfaCode, newPassword, {
        onSuccess() {
          resolve({});
        },
        mfaRequired() {
          resolve({mfaRequired: true});
        },
        onFailure: reject,
      });
    });
  }
}

export default new Auth();


// WEBPACK FOOTER //
// ./src/lib/auth.js